import { Block, Button, ButtonLink, Container, Flex, For, IconFA, If, LoaderFill, Slot, createPage, defaultBreakpoints } from 'react-commons';
import { NextPageContext } from 'next';
import { useEffect, useState } from 'react';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

import FluidLayout from '@/layouts/FluidLayout';
import Meta, { generateGamePageMetaData } from '@/components/Meta';
import { archiveEventListener, DailyArchiveByMonth, getDailyArchiveYears, WeeklyArchive } from '@/components/gamePage/Archive';

import GamesModel, { FullGameData } from '@/lib/drupal/models/Games';
import SwagModel from '@/lib/swag/models/Swag';
import { useAuthStore } from '@/lib/drupal/stores/auth';
import { useGenreBreadcrumbs } from '@/lib/hooks/useGameBreadcrumbs';
import useScreenSize from '@/lib/hooks/useScreenSize';

import style from './index.module.scss';
import { CaptureClickEvent } from '@/lib/util/captureClickEvent';
import { useAnnualPlanPromo } from '@/lib/util/useAnnualPlanPromo';

interface GamesArchivePageProps {
  game: FullGameData
  slug: string
  swagUUID: string
}

export default createPage<GamesArchivePageProps>('GamesArchivePage', { style }, function GamesArchivePage ({ className }, props) {
  const { game } = props;
  const [ authState ] = useAuthStore();
  const [ swagUser, setSwagUser ] = useState(null);

  const genreBreadcrumbs = useGenreBreadcrumbs(game.genres, game.isDownloadGame);
  const metaProps = generateGamePageMetaData(game, genreBreadcrumbs);
  const { width: screenWidth } = useScreenSize();

  const [ maxYears, setMaxYears ] = useState(5);
  const [ yearOffset, setYearOffset ] = useState(0);
  const allYears = getDailyArchiveYears(props.game).reverse();

  useEffect(() => {
    const maxYearsNew = screenWidth > defaultBreakpoints.desktop ? 5 : 3;
    setMaxYears(maxYearsNew);

    if((yearOffset > allYears.length - maxYearsNew)) {
      setYearOffset(allYears.length - maxYearsNew);
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ screenWidth, yearOffset ]);

  useEffect(() => {
    (async () => {
      if (!authState.ready) return;
      const swagUser = await SwagModel.getSwagUser();
      setSwagUser(swagUser);
    })();
  }, [ authState.ready, authState.user ]);

  const { isActive: isPromoActive, promoDescription } = useAnnualPlanPromo();
  
  return (
    <FluidLayout 
      className={className}
      pageName='game_modern_archive'
      pageEventSettings={{
        gameTitle: game.title,
        gameId: game.uid,
      }}
    >

      {/* Meta information */}
      <Meta 
        {...metaProps}
      />

      {
        If(authState.ready && swagUser, () => (
          <section>
            <Container>
              <Block>
                {
                  If(props.game.archive?.frequency === 'daily', () => (
                    <DailyArchiveByMonth 
                      game={props.game}
                      swagUUID={props.swagUUID}
                      entityID={swagUser._id}
                    >
                      <Slot name='title'>{props.game.title} Archive</Slot>
                    </DailyArchiveByMonth>
                  )).Else(() => (
                    <WeeklyArchive 
                      game={props.game}
                      swagUUID={props.swagUUID}
                      entityID={swagUser._id}
                    >
                      <Slot name='title'>{props.game.title} Archive</Slot>
                    </WeeklyArchive>
                  )).EndIf()
                }
              </Block>
            </Container>
          </section>
        )).Else(() => (
          <LoaderFill />
        )).EndIf()
      }

      {
        If(authState.ready && (!authState.user || !authState.user.isPremiumUser), () => (
          <div className='GamesArchivePage__Overlay'>
            <div>
              <h2
                dangerouslySetInnerHTML={{ 
                  __html: promoDescription || 'Decades Worth of Games'
                }}
              />
              {
                If(!isPromoActive, () => (
                  <h4>All at your fingertips!</h4>
                )).EndIf()
              }
              <p>
                A subscription gets you unlimited access to our daily game<br />
                archives and many more features!
              </p>
              <Flex centerX alignCenter directionColumn>
                {
                  If(isPromoActive, () => (
                    <CaptureClickEvent
                      action='upsell_sale'
                      location='overlay_upsell'
                      href='/purchase/subscribe'
                      properties={() => ({
                        tag_name: 'upsell_sale_game_archive',
                        id: props.game.uid,
                        title: props.game.title,
                        view_node: props.game.href,
                      })}
                    >
                      <ButtonLink 
                        rounded
                        medium
                        secondary 
                        href='/purchase/subscribe'
                        className='upsell_sale_game_archive'
                      >
                        Subscribe
                      </ButtonLink>
                    </CaptureClickEvent>
                  )).Else(() => (
                    <CaptureClickEvent
                      action='upsell'
                      location='overlay_upsell'
                      href='/purchase/subscribe'
                      properties={() => ({
                        tag_name: 'upsell_subscribe_button',
                        id: props.game.uid,
                        title: props.game.title,
                        view_node: props.game.href,
                      })}
                    >
                      <ButtonLink 
                        rounded
                        medium
                        secondary 
                        href='/purchase/subscribe'
                        className='upsell_subscribe_button'
                      >
                        Subscribe
                      </ButtonLink>
                    </CaptureClickEvent>
                  )).EndIf()
                }
                
                <ButtonLink
                  rounded
                  medium
                  href={game.href}
                >
                  No Thanks
                </ButtonLink>
              </Flex>
            </div>
          </div>
        )).EndIf()
      }

    </FluidLayout>
  );
});

export async function getServerSideProps (context: NextPageContext) {
  const slug = context.query.slug as string;

  // Get game data
  let game: FullGameData;
  let swagUUID: string;
  try {
    game = await GamesModel.getBySlug('gamelanding/' + slug);
    swagUUID = await SwagModel.getGameId(slug) ?? null;
  } catch (err) {
    throw err;
  }

  // Gather props
  const props: GamesArchivePageProps = {
    game,
    slug,
    swagUUID
  };

  return {
    props,
  };
}